/**
 * dealDiagramsStore - one shared BPR diagram state per DEAL, subscribed to by
 * every embedded surface on the deal page (the pinned System Architecture
 * section, each questionnaire section's process-flow embed).
 *
 * Module-level external store (useSyncExternalStore): one engagement +
 * diagram-batch fetch per deal shared across all section embeds, plus the
 * mutations the embedded workflow needs -
 * - generate: build/reuse the knowledge graph, then generate the diagram
 *   batch (long-running model calls; state carries a `generating` flag);
 * - refreshFutureState: after a questionnaire save, regenerate ONLY the
 *   changed sections' future-state workflows;
 * - ensureSectionDiagrams: top up the process-flow pairs of sections MISSING
 *   from the newest batch (questions captured after the batch was minted).
 * Both section mutations run through ONE serialized queue per deal - keys
 * queued while a call runs drain once it settles - so two mutations can
 * never race two batch versions.
 *
 * Self-gating: non-BPR deals resolve no engagement, so the state stays empty
 * and every embed renders nothing.
 */

import { functionalAreaKeyMatches, type BprDiagramRecord } from '@protoss/shared';
import * as React from 'react';
import { bprApi } from './bprApi';

/** The two sequential phases of a full generation run (drives the meter). */
export type DealDiagramsGenerationPhase = 'graph' | 'diagrams';

export interface DealDiagramsState {
  /** idle = not fetched yet; ready covers both "has diagrams" and "none generated". */
  status: 'idle' | 'loading' | 'ready' | 'error';
  /** Null once ready = not a BPR deal (no linked engagement). */
  engagementUuid: string | null;
  diagrams: BprDiagramRecord[];
  /** Section keys with a future-state refresh in flight (or queued). */
  refreshingSections: ReadonlySet<string>;
  /** Section keys with a process-flow pair top-up in flight (or queued). */
  generatingSections: ReadonlySet<string>;
  /**
   * Section keys whose flow failed validation in a run this page session. The
   * error is already on screen, so the auto top-up skips them instead of
   * re-running the slow cascade; Regenerate (or the next visit) retries them.
   */
  failedSectionKeys: ReadonlySet<string>;
  /** True while a full graph-build + diagram generation runs. */
  generating: boolean;
  /** Which generation step is running (null when not generating). */
  generationPhase: DealDiagramsGenerationPhase | null;
  /** Date.now() when the run / the current phase started (progress meter). */
  generationStartedAt: number | null;
  generationPhaseStartedAt: number | null;
  error: string | null;
}

const INITIAL_STATE: DealDiagramsState = {
  status: 'idle',
  engagementUuid: null,
  diagrams: [],
  refreshingSections: new Set<string>(),
  generatingSections: new Set<string>(),
  failedSectionKeys: new Set<string>(),
  generating: false,
  generationPhase: null,
  generationStartedAt: null,
  generationPhaseStartedAt: null,
  error: null,
};

interface DealEntry {
  state: DealDiagramsState;
  listeners: Set<() => void>;
  loadPromise: Promise<void> | null;
  refreshInFlight: boolean;
  pendingRefreshKeys: Set<string>;
  pendingEnsureKeys: Set<string>;
  /** In-flight or settled graph build started ahead of the diagram batch (prebuildDealGraph). */
  graphPromise: Promise<unknown> | null;
}

const entries = new Map<string, DealEntry>();

function entryFor(dealUuid: string): DealEntry {
  let entry = entries.get(dealUuid);
  if (!entry) {
    entry = {
      state: INITIAL_STATE,
      listeners: new Set(),
      loadPromise: null,
      refreshInFlight: false,
      pendingRefreshKeys: new Set(),
      pendingEnsureKeys: new Set(),
      graphPromise: null,
    };
    entries.set(dealUuid, entry);
  }
  return entry;
}

function setState(dealUuid: string, patch: Partial<DealDiagramsState>): void {
  const entry = entryFor(dealUuid);
  entry.state = { ...entry.state, ...patch };
  for (const listener of entry.listeners) listener();
}

/**
 * Map a diagram mutation failure to a human message (the sowExportErrorMessage
 * precedent). Substring match: some callers wrap the whole response body text.
 */
export function diagramErrorMessage(err: unknown): string {
  const raw = err instanceof Error ? err.message : '';
  if (raw.includes('BPR_DIAGRAM_GENERATION_INVALID_OUTPUT')) {
    return 'The AI could not produce valid diagrams for one or more sections. Try Regenerate - a rerun usually succeeds.';
  }
  if (raw.includes('BPR_DIAGRAM_RUN_TIMED_OUT')) {
    return 'Diagram generation took too long and was stopped. Try Regenerate.';
  }
  return raw || 'Diagram generation failed.';
}

/** Surface a partial failure (some sections failed validation, the rest persisted). */
function failedSectionsMessage(failedSectionKeys: string[]): string | null {
  if (failedSectionKeys.length === 0) return null;
  const n = failedSectionKeys.length;
  return `Diagrams for ${n} section${n === 1 ? '' : 's'} could not be generated this run. Try Regenerate - a rerun usually succeeds.`;
}

/** Kick off the one engagement + diagrams fetch for a deal (idempotent). */
export function ensureDealDiagramsLoaded(dealUuid: string): Promise<void> {
  const entry = entryFor(dealUuid);
  if (entry.loadPromise) return entry.loadPromise;
  setState(dealUuid, { status: 'loading', error: null });
  entry.loadPromise = (async () => {
    try {
      const engagement = await bprApi.getEngagementByDeal(dealUuid);
      if (!engagement) {
        setState(dealUuid, { status: 'ready', engagementUuid: null, diagrams: [] });
        return;
      }
      const diagrams = await bprApi.getDiagrams(engagement.uuid);
      setState(dealUuid, { status: 'ready', engagementUuid: engagement.uuid, diagrams });
    } catch (err) {
      // Errors must not break the questionnaire; allow a later retry.
      entry.loadPromise = null;
      setState(dealUuid, {
        status: 'error',
        error: err instanceof Error ? err.message : 'Failed to load BPR diagrams.',
      });
    }
  })();
  return entry.loadPromise;
}

/**
 * Start the knowledge-graph build EARLY - while discovery still runs - so it
 * overlaps discovery instead of sitting on the critical path; the diagram
 * batch itself waits for discovery to settle and then reuses this build
 * (COS-129). Silent (no generating state); idempotent while a build is in
 * flight or done. A failure clears it, so generateDealDiagrams rebuilds and
 * surfaces the error.
 */
export function prebuildDealGraph(dealUuid: string): void {
  const entry = entryFor(dealUuid);
  const engagementUuid = entry.state.engagementUuid;
  if (!engagementUuid || entry.graphPromise) return;
  const build = bprApi.buildGraph(engagementUuid, false);
  entry.graphPromise = build;
  build.catch(() => {
    if (entry.graphPromise === build) entry.graphPromise = null;
  });
}

/**
 * Build (or reuse) the knowledge graph, then generate the diagram batch.
 * Long-running (one model call per diagram); errors land on state.error.
 */
export async function generateDealDiagrams(dealUuid: string, force = false): Promise<void> {
  const entry = entryFor(dealUuid);
  const engagementUuid = entry.state.engagementUuid;
  if (!engagementUuid || entry.state.generating) return;
  const startedAt = Date.now();
  setState(dealUuid, {
    generating: true,
    generationPhase: 'graph',
    generationStartedAt: startedAt,
    generationPhaseStartedAt: startedAt,
    error: null,
  });
  try {
    // Reuse a graph build prebuildDealGraph already started (it overlapped
    // discovery); the server serves the cached version when nothing changed.
    const graphBuild = entry.graphPromise ?? bprApi.buildGraph(engagementUuid, false);
    entry.graphPromise = null;
    await graphBuild;
    setState(dealUuid, { generationPhase: 'diagrams', generationPhaseStartedAt: Date.now() });
    // A fresh generation is an async run server-side; bprApi polls it to
    // completion, so this await still spans the whole generation.
    const result = await bprApi.generateDiagrams(engagementUuid, force);
    setState(dealUuid, {
      diagrams: result.diagrams,
      generating: false,
      generationPhase: null,
      generationStartedAt: null,
      generationPhaseStartedAt: null,
      // Partial success: the batch persisted minus the failed sections - show
      // what was lost instead of pretending the run was clean. A full run
      // covers every section, so its failures replace the earlier ones.
      error: failedSectionsMessage(result.failedSectionKeys),
      failedSectionKeys: new Set(result.failedSectionKeys),
    });
  } catch (err) {
    setState(dealUuid, {
      generating: false,
      generationPhase: null,
      generationStartedAt: null,
      generationPhaseStartedAt: null,
      error: diagramErrorMessage(err),
    });
  }
}

/**
 * Queue a future-state refresh for the sections whose questionnaire answers
 * were just saved. No-ops (silently) when the deal has no engagement or no
 * generated diagrams yet - a save must never surface a BPR error.
 */
export function refreshDealFutureState(dealUuid: string, sectionKeys: string[]): void {
  if (sectionKeys.length === 0) return;
  const entry = entryFor(dealUuid);
  void ensureDealDiagramsLoaded(dealUuid).then(() => {
    const { engagementUuid, diagrams } = entry.state;
    if (!engagementUuid || diagrams.length === 0) return;
    // Only sections that actually have a workflow diagram can refresh. Match
    // resiliently so a section whose key drifted after the batch was minted
    // (business-process rename) still resolves to its stored workflow.
    const diagrammedKeys = diagrams
      .filter((d) => d.kind === 'workflow' && d.functionalAreaKey)
      .map((d) => d.functionalAreaKey as string);
    const keys = sectionKeys.filter((k) =>
      diagrammedKeys.some((dk) => functionalAreaKeyMatches(dk, k))
    );
    if (keys.length === 0) return;
    for (const key of keys) entry.pendingRefreshKeys.add(key);
    setState(dealUuid, {
      refreshingSections: new Set([...entry.state.refreshingSections, ...keys]),
    });
    void drainSectionDiagramQueue(dealUuid);
  });
}

/**
 * Queue creation of the process-flow pairs for sections with NO workflow in
 * the newest batch (their questions were captured after the batch was minted,
 * or the section was added later) - the section-flow half of "diagrams appear
 * with the architecture, without a manual Regenerate". No-ops (silently) when
 * the deal has no engagement or nothing was generated yet: the full
 * auto-generation covers the empty case, and it already emits every section's
 * pair alongside the architecture.
 */
export function ensureSectionDiagrams(dealUuid: string, sectionKeys: string[]): void {
  if (sectionKeys.length === 0) return;
  const entry = entryFor(dealUuid);
  void ensureDealDiagramsLoaded(dealUuid).then(() => {
    const { engagementUuid, diagrams } = entry.state;
    if (!engagementUuid || diagrams.length === 0) return;
    // Only sections MISSING a workflow top up (resilient key match, so a
    // batch minted under a renamed key is not regenerated needlessly).
    const diagrammedKeys = diagrams
      .filter((d) => d.kind === 'workflow' && d.functionalAreaKey)
      .map((d) => d.functionalAreaKey as string);
    const keys = sectionKeys.filter(
      (k) => !diagrammedKeys.some((dk) => functionalAreaKeyMatches(dk, k))
    );
    if (keys.length === 0) return;
    for (const key of keys) entry.pendingEnsureKeys.add(key);
    setState(dealUuid, {
      generatingSections: new Set([...entry.state.generatingSections, ...keys]),
    });
    void drainSectionDiagramQueue(dealUuid);
  });
}

/**
 * Serialize section-diagram mutations per deal (two mutations racing would
 * fork batch versions); batches queued keys into one call per kind per drain.
 * Top-ups run before future refreshes so a save in a section still being
 * created lands on the fresh pair.
 */
async function drainSectionDiagramQueue(dealUuid: string): Promise<void> {
  const entry = entryFor(dealUuid);
  if (entry.refreshInFlight) return;
  if (entry.pendingEnsureKeys.size === 0 && entry.pendingRefreshKeys.size === 0) return;
  const engagementUuid = entry.state.engagementUuid;
  if (!engagementUuid) return;
  const ensureKeys = [...entry.pendingEnsureKeys];
  const refreshKeys = [...entry.pendingRefreshKeys];
  entry.pendingEnsureKeys.clear();
  entry.pendingRefreshKeys.clear();
  entry.refreshInFlight = true;
  try {
    if (ensureKeys.length > 0) {
      try {
        const result = await bprApi.ensureSectionDiagrams(engagementUuid, ensureKeys);
        setState(dealUuid, {
          diagrams: result.diagrams,
          error: failedSectionsMessage(result.failedSectionKeys),
          failedSectionKeys: new Set([
            ...entry.state.failedSectionKeys,
            ...result.failedSectionKeys,
          ]),
        });
      } catch (err) {
        // Best-effort (never rethrow - a questionnaire save must not hard-fail
        // on diagram work), but VISIBLE: a missed top-up is retried on the next
        // questionnaire reload or corrected by a full regenerate.
        setState(dealUuid, { error: diagramErrorMessage(err) });
      }
    }
    if (refreshKeys.length > 0) {
      try {
        const result = await bprApi.refreshSectionFutureState(engagementUuid, refreshKeys);
        setState(dealUuid, {
          diagrams: result.diagrams,
          error: failedSectionsMessage(result.failedSectionKeys),
        });
      } catch (err) {
        // Best-effort (the save already succeeded; stale future state is
        // corrected by the next save or a full regenerate), but VISIBLE.
        setState(dealUuid, { error: diagramErrorMessage(err) });
      }
    }
  } finally {
    entry.refreshInFlight = false;
    setState(dealUuid, {
      refreshingSections: new Set(entry.pendingRefreshKeys),
      generatingSections: new Set(entry.pendingEnsureKeys),
    });
    if (entry.pendingEnsureKeys.size > 0 || entry.pendingRefreshKeys.size > 0)
      void drainSectionDiagramQueue(dealUuid);
  }
}

function subscribe(dealUuid: string, listener: () => void): () => void {
  const entry = entryFor(dealUuid);
  entry.listeners.add(listener);
  return () => {
    entry.listeners.delete(listener);
  };
}

/** The deal's shared BPR diagram state (auto-loads on first use). */
export function useDealDiagrams(dealUuid: string): DealDiagramsState {
  const state = React.useSyncExternalStore(
    React.useCallback((listener: () => void) => subscribe(dealUuid, listener), [dealUuid]),
    () => entryFor(dealUuid).state
  );
  React.useEffect(() => {
    if (dealUuid) void ensureDealDiagramsLoaded(dealUuid);
  }, [dealUuid]);
  return state;
}

/**
 * The sections the auto top-up should generate a flow for: no workflow in the
 * batch yet (resilient key match) and not a section that just failed
 * validation this page session (that failure is already shown; re-running it
 * automatically would repeat the slow retry cascade).
 */
export function sectionsMissingFlow(
  sectionKeys: string[],
  diagrams: BprDiagramRecord[],
  failedSectionKeys: ReadonlySet<string>
): string[] {
  const diagrammedKeys = diagrams
    .filter((d) => d.kind === 'workflow' && d.functionalAreaKey)
    .map((d) => d.functionalAreaKey as string);
  const failed = [...failedSectionKeys];
  return sectionKeys.filter(
    (key) =>
      !diagrammedKeys.some((dk) => functionalAreaKeyMatches(dk, key)) &&
      !failed.some((fk) => functionalAreaKeyMatches(fk, key))
  );
}

/** The section's workflow pair from a loaded batch (state null = legacy current). */
export function sectionWorkflows(
  diagrams: BprDiagramRecord[],
  sectionKey: string
): { current: BprDiagramRecord | null; future: BprDiagramRecord | null } {
  let current: BprDiagramRecord | null = null;
  let future: BprDiagramRecord | null = null;
  for (const d of diagrams) {
    if (d.kind !== 'workflow' || !functionalAreaKeyMatches(d.functionalAreaKey, sectionKey)) continue;
    if (d.state === 'future') future = d;
    else current = current ?? d;
  }
  return { current, future };
}
